import { gql } from "@codesandbox/api";
import { useSandboxEditor } from "features/editor/Feature";
import type { SandboxAuthor, Team } from "features/types/sandbox";
import {
  Icon,
  onGridPx,
  Stack,
  Button,
  styled,
  Tooltip,
  Text,
  Select,
} from "prism-react";
import type { FC } from "react";
import { useState, useContext } from "react";

import { paths } from "utils/router";

import { shareContext } from "../ShareSandboxProvider";
import type { CollaboratorFragment, InvitationFragment } from "../api";
import {
  useChangeCollaboratorAuthorization,
  useChangeInvitationAuthorization,
  useRemoveCollaborator,
  useRevokeCollaboratorInvitation,
} from "../api";
import { StyledAvatar } from "../blocks/StyledAvatar";

export const ContributorItem: FC<
  (
    | Pick<CollaboratorFragment, "authorization" | "user">
    | Pick<InvitationFragment, "authorization" | "email">
  ) & {
    canChangePermissions: boolean;
    onPermissionsChange?: (
      permissions: gql.Authorization | "REMOVE",
    ) => Promise<unknown>;
  }
> = ({ onPermissionsChange, canChangePermissions, ...props }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const getAvatar = () => {
    if ("user" in props) {
      const { user } = props as CollaboratorFragment;
      return (
        <>
          <StyledAvatar
            avatarSrc={user.avatarUrl}
            name={user.name || user.username}
          />
          <Text color="neutral-fg-subtle">
            {user.name?.trim() || user.username}
          </Text>
        </>
      );
    }

    const { email } = props as InvitationFragment;

    return (
      <>
        <StyledAvatar
          avatarSrc={null}
          icon={<Icon size={3} type="git" />}
          name="email"
        />
        <Text color="neutral-fg-subtle">{email}</Text>
      </>
    );
  };

  const isOwner = props.authorization === gql.Authorization.OWNER;

  return (
    <Stack
      css={{
        justifyContent: "space-between",
      }}
      horizontal
    >
      <Stack horizontal>{getAvatar()}</Stack>

      {canChangePermissions && !isOwner ? (
        <Stack gap={2} horizontal>
          <Select
            disabled={loading}
            onValueChange={(value: gql.Authorization | "REMOVE") => {
              setLoading(true);

              onPermissionsChange?.(value).finally(() => {
                setLoading(false);
              });
            }}
            value={props.authorization}
          >
            <Select.Item value={gql.Authorization.WRITE_CODE}>
              Can Edit
            </Select.Item>

            <Select.Item value={gql.Authorization.READ}>Can View</Select.Item>

            <Select.Separator />

            <Select.Item value="REMOVE">Remove</Select.Item>
          </Select>
        </Stack>
      ) : isOwner ? (
        <span>owner</span>
      ) : null}
    </Stack>
  );
};

export const ContributorTeam: FC<{
  team: Team;
}> = ({ team }) => {
  return (
    <Stack
      css={{
        justifyContent: "space-between",
      }}
      horizontal
    >
      <Stack horizontal>
        <StyledAvatar
          avatarSrc={team.avatarUrl || null}
          name={team.name}
          isWorkspace
        />

        <Stack gap={1} horizontal>
          <Text
            css={{
              color: "$neutral-fg-subtle",
            }}
          >
            {team.name}
          </Text>
          <Tooltip content="Manage workspace collaborators">
            <Button
              as="a"
              href={paths.dashboard.workspaceSettings(team.id)}
              icon="settings"
              rel="noreferrer noopener"
              target="_blank"
            />
          </Tooltip>
        </Stack>
      </Stack>

      <span>can access</span>
    </Stack>
  );
};

const StyledContributorsList = styled("ul", {
  display: "grid",
  gridTemplateRows: "repeat(auto-fill, 1fr)",
  listStyle: "none",
  margin: 0,
  padding: 0,
  gridAutoRows: onGridPx(7), // fixed row
});

const StyledContributorsLi = styled("li", {
  display: "flex",
  alignItems: "center",
  width: "100%",

  "> *": {
    flex: 1,
  },
});

/**
 * Renders a list of contributors with the corresponding permissions.
 */
export const ContributorsUI: FC<{
  contributors: Array<CollaboratorFragment | InvitationFragment>;
  onPermissionsChange: (
    contributor: CollaboratorFragment | InvitationFragment | SandboxAuthor,
    permission: gql.Authorization | "REMOVE",
  ) => Promise<unknown>;
  canChangePermissions: boolean;
  sandboxOwner?: SandboxAuthor | null;
  sandboxTeam?: Team | null;
}> = ({
  contributors,
  onPermissionsChange,
  canChangePermissions,
  sandboxOwner,
  sandboxTeam,
}) => {
  return (
    <StyledContributorsList>
      {sandboxTeam && (
        <StyledContributorsLi key="CSB-team">
          <ContributorTeam team={sandboxTeam} />
        </StyledContributorsLi>
      )}
      {sandboxOwner && (
        <StyledContributorsLi>
          <ContributorItem
            authorization={gql.Authorization.OWNER}
            canChangePermissions={canChangePermissions}
            onPermissionsChange={(newPermissions) =>
              onPermissionsChange(sandboxOwner, newPermissions)
            }
            user={sandboxOwner}
          />
        </StyledContributorsLi>
      )}
      {contributors.map((contributor) => (
        <StyledContributorsLi
          key={
            (contributor as CollaboratorFragment).user?.name ||
            (contributor as InvitationFragment)?.email
          }
        >
          <ContributorItem
            {...contributor}
            canChangePermissions={canChangePermissions}
            onPermissionsChange={(newPermissions) =>
              onPermissionsChange(contributor, newPermissions)
            }
          />
        </StyledContributorsLi>
      ))}
    </StyledContributorsList>
  );
};

/**
 * @todo rename Contributors to Collaborators
 * @todo users that are Collaborators but not owners, shouldn't be able to change permissions
 */
export const Contributors = ({
  collaborators,
  invitations,
  refetchCollaborators,
}: {
  collaborators: CollaboratorFragment[];
  invitations: InvitationFragment[];
  refetchCollaborators: () => void;
}) => {
  const { sandboxId, canChangePermissions } = useContext(shareContext);

  const [, changeCollaboratorAuthorization] =
    useChangeCollaboratorAuthorization(sandboxId);
  const [, removeCollaborator] = useRemoveCollaborator(sandboxId);
  const [, changeInvitationAuthorization] =
    useChangeInvitationAuthorization(sandboxId);
  const [, revokeCollaboratorInvitation] =
    useRevokeCollaboratorInvitation(sandboxId);

  const project = useSandboxEditor();

  const sandboxOwner = project.author;
  const sandboxTeam = project.team;

  let contributors: Array<CollaboratorFragment | InvitationFragment> = [];

  if (sandboxOwner) {
    contributors = collaborators.filter((c) => c.user.id !== sandboxOwner.id);
  }

  const pendingInvitations = invitations;

  return (
    <ContributorsUI
      canChangePermissions={canChangePermissions}
      contributors={[...contributors, ...pendingInvitations]}
      onPermissionsChange={(collaboratorOrInvitation, authorization) => {
        if (!("user" in collaboratorOrInvitation)) {
          const invitation = collaboratorOrInvitation as InvitationFragment;
          if (authorization === "REMOVE") {
            return revokeCollaboratorInvitation(invitation.id!).then(
              refetchCollaborators,
            );
          } else {
            return changeInvitationAuthorization({
              invitationId: invitation.id!,
              authorization,
            }).then(refetchCollaborators);
          }
        } else {
          const collaborator = collaboratorOrInvitation as CollaboratorFragment;
          if (authorization === "REMOVE") {
            return removeCollaborator(collaborator.user.username).then(
              refetchCollaborators,
            );
          }

          return changeCollaboratorAuthorization({
            username: collaborator.user.username,
            authorization,
          }).then(refetchCollaborators);
        }
      }}
      sandboxOwner={sandboxOwner}
      sandboxTeam={sandboxTeam}
    />
  );
};
